/**
 * Separate forgot-password page for the Ailaunch / signup-without-payment flow.
 * Does not modify the current ForgotPassword page.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/auth.service';

const ForgotPasswordAilaunch: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (password: string) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/\d/.test(password)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return 'Password must contain at least one special character';
    return '';
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await AuthService.forgotPassword(email);
      setStep(1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError('');
    setError('');
    const passwordValidationError = validatePassword(formData.password);
    if (passwordValidationError) {
      setError(passwordValidationError);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await AuthService.confirmForgotPassword(email, formData.code, formData.password);
      navigate('/login-ai-flow', { replace: true });
    } catch (err: any) {
      setCodeError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'password') setPasswordError(validatePassword(value));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') setError(value !== formData.password ? 'Passwords do not match' : '');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDF2F4',
        p: 1.5,
      }}
    >
      <Box sx={{ maxWidth: 480, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        <Box sx={{ textAlign: 'center', width: '100%', mb: 1.5 }}>
          <Typography
            component="h1"
            sx={{
              // fontFamily: 'inherit',
              fontSize: { xs: '1.75rem', sm: '2rem' },
              fontWeight: 700,
              color: '#2C303E',
              marginBottom: 0.5,
              lineHeight: 1.2,
            }}
          >
            {step === 0 ? 'Reset your password' : 'Enter verification code'}
          </Typography>
          <Typography
            sx={{
              // fontFamily: 'inherit',
              fontSize: '1rem',
              fontWeight: 400,
              color: '#5B6171',
              lineHeight: 1.3,
            }}
          >
            {step === 0 ? 'Enter your email address to receive a reset code.' : 'Enter the 6-digit code from your email and set a new password.'}
          </Typography>
        </Box>

        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            p: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 4,
            bgcolor: '#ffffff',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              minHeight: 44,
              '& .MuiOutlinedInput-input': { py: 1.25, fontSize: '1rem' },
              '& fieldset': { borderColor: '#D1D5DB' },
              '&:hover fieldset': { borderColor: '#9ca3af' },
              '&.Mui-focused': {
                boxShadow: 'none',
                '& fieldset': { borderColor: '#9B51E0', borderWidth: 1, boxShadow: 'none' },
              },
            },
            '& .MuiInputLabel-root': {
              color: '#9B51E0',
              fontSize: '1rem',
              fontWeight: 400,
              '&.Mui-focused': { color: '#9B51E0' },
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{ mb: 1, cursor: 'pointer', display: 'inline-block', fontSize: '0.875rem', color: '#9B51E0' }}
            onClick={() => navigate('/login-ai-flow')}
          >
            ← Back to log in
          </Typography>

          {step === 0 ? (
            <Box component="form" onSubmit={handleEmailSubmit}>
              <TextField
                label="Email address"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!error}
                helperText={error}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                autoComplete="email"
                disabled={isLoading}
                sx={{ mb: 1.5 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
                sx={{
                  mt: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: '#9B51E0',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { bgcolor: '#8B41D0' },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send reset code'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerificationSubmit}>
              <TextField
                label="Code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                error={!!codeError}
                helperText={codeError}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                autoComplete="one-time-code"
                disabled={isLoading}
                sx={{ mb: 1.5 }}
              />
              <TextField
                label="New password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                error={!!passwordError}
                helperText={passwordError}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                autoComplete="new-password"
                disabled={isLoading}
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" aria-label="Toggle password" sx={{ color: '#757575' }}>
                        {showPassword ? <VisibilityOffIcon sx={{ color: 'inherit' }} /> : <VisibilityIcon sx={{ color: 'inherit' }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm new password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={!!error && formData.confirmPassword !== ''}
                helperText={formData.confirmPassword !== '' ? error : ''}
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                autoComplete="new-password"
                disabled={isLoading}
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((s) => !s)} edge="end" aria-label="Toggle confirm password" sx={{ color: '#757575' }}>
                        {showConfirmPassword ? <VisibilityOffIcon sx={{ color: 'inherit' }} /> : <VisibilityIcon sx={{ color: 'inherit' }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem', color: '#888C95' }}>
                Didn&apos;t receive the code?{' '}
                <Typography
                  component="span"
                  sx={{ color: '#9B51E0', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => AuthService.forgotPassword(email)}
                >
                  Resend code
                </Typography>
              </Typography>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
                sx={{
                  mt: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: '#9B51E0',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { bgcolor: '#8B41D0' },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Reset password'}
              </Button>
            </Box>
          )}

          <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.875rem', fontWeight: 400, color: '#888C95' }}>
            Remember your password?{' '}
            <Typography
              component="span"
              sx={{ color: '#9B51E0', cursor: 'pointer', fontWeight: 400, textDecoration: 'underline' }}
              onClick={() => navigate('/login-ai-flow')}
            >
              Log in
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordAilaunch;
